import { yupResolver } from "@hookform/resolvers/yup";
import { useFetchQuery, useMutation } from "hooks/useQueries";
import { useForm } from "react-hook-form";
import axiosInstance from "utils/axiosInstance";
import { refetchQuery } from "utils/helpers";
import { QueryKeys } from "utils/queryKey";
import { addEmployeeSchema } from "utils/validationSchema";

type FormValue = {
  user?: {
    personalDetails?: {
      email?: string;
      name?: string;
      dateOfBirth?: string;
      aadhar?: string;
      emergencyContact?: string;
      emergencyContactNum?: string;
    };
    phone?: string;
    countryCode?: string;
  };
  salary?: 0;
  position?: string;
};

const useAddEmployee = ({ setOpen, selectedId, setSelectedId }: any) => {
  /** ===================================================================== */
  const { data: emplyeeDetials } = useFetchQuery<{
    data: {
      user: {
        personalDetails: {
          id: number;
          dateOfBirth: any;
          email: string;
          name: string;
          gender: string;
          maritalStatus: string;
          fatherName: string;
          motherName: string;
          address: any;
          city: any;
          state: any;
          country: any;
          pincode: any;
          income: any;
          occupation: any;
          pan: any;
          aadhar: string;
          emergencyContact: string;
          emergencyContactCountryCode: string;
          emergencyContactNum: string;
          gstn: any;
        };
        phone: string;
        id: number;
        newUser: boolean;
        grantAccess: boolean;
        countryCode: string;
      };
      salary: number;
      branchName: string;
      code: string;
      position: string;
      id: number;
    };
  }>({
    key: ["GET_EMPLOYEE_BY_ID"],
    route: `/employees/${selectedId?.id}/`,
    enabled: Boolean(selectedId?.id),
  });
  /** ===================================================================== */

  const form = useForm<FormValue>({
    defaultValues: {
      user: {
        personalDetails: {
          email: "",
          name: "",
          dateOfBirth: "",
          aadhar: "",
          emergencyContact: "",
          emergencyContactNum: "",
        },
        phone: "",
        countryCode: "+91",
      },
      salary: 0,
      position: "",
    },
    resolver: yupResolver(addEmployeeSchema),
    values: {
      user: {
        personalDetails: {
          email: emplyeeDetials?.data?.user?.personalDetails?.email || "",
          name: emplyeeDetials?.data?.user?.personalDetails?.name || "",
          dateOfBirth:
            emplyeeDetials?.data?.user?.personalDetails?.dateOfBirth || "",
          aadhar: emplyeeDetials?.data?.user?.personalDetails?.aadhar || "",
          emergencyContact:
            emplyeeDetials?.data?.user?.personalDetails?.emergencyContact || "",
          emergencyContactNum:
            emplyeeDetials?.data?.user?.personalDetails?.emergencyContactNum ||
            "",
        },
        phone: emplyeeDetials?.data?.user?.phone || "",
        countryCode: emplyeeDetials?.data?.user?.countryCode || "+91",
      },
      salary: 0,
      position: "",
    },
  });

  /** ===================================================================== */
  const { mutate: handleUpdateEmployee } = useMutation({
    mutationKey: ["UPDATE_EMPLOYEE"],
    mutationFn: (data: any) =>
      axiosInstance.patch(`/employees/${selectedId?.id}/`, data),
    onSuccess: () => {
      setOpen(false);
      form.reset();
      setSelectedId(null);
      refetchQuery(QueryKeys.EMPLOYEE_LIST);
    },
  });
  /** ===================================================================== */
  const { mutate: handleCreateEmployee, isLoading: isSubmitting } = useMutation(
    {
      mutationFn: (data: any) => axiosInstance.post(`/employees/`, data),
      onSuccess: () => {
        form.reset();
        setSelectedId(null);
        setOpen(false);
        refetchQuery(QueryKeys.EMPLOYEE_LIST);
      },
    },
  );

  const onSubmit = form.handleSubmit((values) =>
    selectedId?.id
      ? handleUpdateEmployee(values)
      : handleCreateEmployee(values),
  );

  return { isSubmitting, form, onSubmit };
};

export default useAddEmployee;
